import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Stack, Typography } from "@mui/material";

/**
 * Discount Report charts — calm, readable D3 visuals.
 * Plot stays clean; values live in a summary strip + hover tooltip.
 */

const COLORS = {
	axis: "#90a4ae",
	grid: "#eef1f4",
	domain: "#e0e5ea",
	label: "#607d8b",
	ink: "#263238",
	muted: "#78909c",
	surface: "#ffffff",
	tooltipBg: "#1c2430",
};

const useContainerSize = (minH = 220) => {
	const ref = useRef(null);
	const [size, setSize] = useState({ width: 0, height: minH });

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		const measure = () => {
			const w = el.clientWidth || el.getBoundingClientRect().width || 0;
			const h = Math.max(el.clientHeight || minH, minH);
			setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [minH]);

	return [ref, size];
};

const ensureTooltip = () => {
	let tip = d3.select("body").select(".dr-d3-tip");
	if (tip.empty()) {
		tip = d3
			.select("body")
			.append("div")
			.attr("class", "dr-d3-tip")
			.style("position", "fixed")
			.style("pointer-events", "none")
			.style("z-index", "10000")
			.style("opacity", "0")
			.style("background", COLORS.tooltipBg)
			.style("color", "#fff")
			.style("padding", "10px 12px")
			.style("border-radius", "10px")
			.style("font-size", "12px")
			.style("line-height", "1.45")
			.style("box-shadow", "0 8px 24px rgba(15,23,42,0.28)")
			.style("max-width", "340px")
			.style("transition", "opacity 120ms ease");
	}
	return tip;
};

const showTip = (html, event) => {
	const tip = ensureTooltip();
	tip.html(html).style("opacity", "1");
	const node = tip.node();
	const tw = node?.offsetWidth || 160;
	const th = node?.offsetHeight || 60;
	const pad = 14;
	let left = event.clientX + pad;
	let top = event.clientY - th - 8;
	if (left + tw > window.innerWidth - 8) left = event.clientX - tw - pad;
	if (top < 8) top = event.clientY + pad;
	tip.style("left", `${left}px`).style("top", `${top}px`);
};

const hideTip = () => {
	d3.select("body").select(".dr-d3-tip").style("opacity", "0");
};

const tickStep = (n, max = 7) => Math.max(1, Math.ceil(n / max));

const SummaryStrip = ({ items = [] }) => {
	if (!items.length) return null;
	return (
		<Stack
			direction="row"
			spacing={0}
			flexWrap="wrap"
			useFlexGap
			sx={{
				mb: 1.25,
				gap: "8px 20px",
			}}>
			{items.map((item) => (
				<Box key={item.key} sx={{ minWidth: 0 }}>
					<Typography
						sx={{
							fontSize: 10,
							letterSpacing: "0.06em",
							textTransform: "uppercase",
							color: COLORS.muted,
							fontWeight: 600,
							lineHeight: 1.2,
						}}>
						{item.label}
					</Typography>
					<Typography
						sx={{
							fontSize: 15,
							fontWeight: 600,
							color: item.color || COLORS.ink,
							lineHeight: 1.3,
							mt: 0.15,
						}}>
						{item.value}
					</Typography>
				</Box>
			))}
		</Stack>
	);
};

const LegendRow = ({ items = [] }) => {
	if (!items.length) return null;
	return (
		<Stack
			direction="row"
			flexWrap="wrap"
			useFlexGap
			spacing={0}
			justifyContent="flex-start"
			sx={{ mt: 1.25, gap: "8px 16px" }}>
			{items.map((item) => (
				<Stack key={item.key || item.label} direction="row" alignItems="center" spacing={0.75}>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: "50%",
							bgcolor: item.color,
							flexShrink: 0,
						}}
					/>
					<Typography sx={{ fontSize: 12, color: COLORS.label, fontWeight: 500 }}>
						{item.label}
						{item.meta ? (
							<Box component="span" sx={{ color: COLORS.ink, fontWeight: 600, ml: 0.5 }}>
								{item.meta}
							</Box>
						) : null}
					</Typography>
				</Stack>
			))}
		</Stack>
	);
};

const styleAxis = (selection, { rotate = false } = {}) => {
	selection.select(".domain").attr("stroke", COLORS.domain);
	selection.selectAll("line").attr("stroke", COLORS.domain);
	selection
		.selectAll("text")
		.style("font-size", "10px")
		.style("fill", COLORS.label)
		.style("font-family", "inherit");
	if (rotate) {
		selection
			.selectAll("text")
			.attr("transform", "rotate(-28)")
			.style("text-anchor", "end");
	}
};

/** Daily multi-series trend (optional discount period color bands) */
export const D3LineChart = ({
	labels = [],
	series = [],
	height = 280,
	valueFormatter = (v) => String(v ?? ""),
	/** Optional per-point HTML shown on hover */
	tooltips = null,
	/** Optional discount timeframe bands: { startIdx, endIdx, bg, border, name, detailHtml } */
	bands = [],
}) => {
	const margin = { top: 12, right: 12, bottom: 40, left: 58 };
	const plotH = Math.max(height - 72, 180);
	const [ref, size] = useContainerSize(plotH);
	const svgRef = useRef(null);

	const summary = useMemo(() => {
		const items = [];
		series.forEach((s) => {
			const vals = (s.values || []).map((v) => Number(v) || 0);
			if (!vals.length) return;
			// Avg over days with activity so padded zero days don't crush ASP/AOV/ROS.
			const active = vals.filter((v) => v > 0);
			const avg = active.length
				? active.reduce((a, b) => a + b, 0) / active.length
				: 0;
			items.push({
				key: `${s.label}-avg`,
				label: `${s.label} · avg`,
				value: valueFormatter(avg),
				color: s.color,
			});
		});
		if (bands.length) {
			items.push({
				key: "frames",
				label: "Discounts",
				value: String(bands.length),
				color: COLORS.muted,
			});
		}
		return items;
	}, [series, valueFormatter, bands.length]);

	const legend = useMemo(
		() =>
			series.map((s) => ({
				key: s.label,
				label: s.label,
				color: s.color,
			})),
		[series]
	);

	useEffect(() => {
		const width = size.width;
		if (width < 48 || !labels.length || !series.length || !svgRef.current) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", plotH).attr("viewBox", `0 0 ${width} ${plotH}`);

		const innerW = width - margin.left - margin.right;
		const innerH = plotH - margin.top - margin.bottom;
		const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

		const data = labels.map((label, i) => {
			const row = { label, i };
			series.forEach((s, si) => {
				row[`v${si}`] = Number(s.values[i]) || 0;
			});
			return row;
		});

		const maxY = d3.max(series, (s) => d3.max(s.values, (v) => Number(v) || 0)) || 0;
		const x = d3.scalePoint().domain(labels).range([0, innerW]).padding(0.15);
		const y = d3
			.scaleLinear()
			.domain([0, maxY * 1.08 || 1])
			.nice()
			.range([innerH, 0]);

		// Discount period color bands (calendar days):
		// start date D → left at tick D (00:00 of D)
		// end date E → right at tick E+1 (23:59:59 of E), so close-on-21 meets start-on-22 at 22.
		(bands || []).forEach((band) => {
			const start = Math.min(band.startIdx, band.endIdx);
			const end = Math.max(band.startIdx, band.endIdx);
			const xStart = x(labels[start]);
			const xEnd = x(labels[end]);
			if (xStart == null || xEnd == null) return;
			const xNext = end < labels.length - 1 ? x(labels[end + 1]) : null;
			const step =
				end < labels.length - 1
					? Math.abs((xNext ?? xEnd) - xEnd)
					: end > 0
						? Math.abs(xEnd - (x(labels[end - 1]) ?? xEnd))
						: 8;
			const left = xStart;
			const right = xNext == null ? Math.min(innerW, xEnd + step) : xNext;
			g.append("rect")
				.attr("x", left)
				.attr("y", 0)
				.attr("width", Math.max(right - left, 2))
				.attr("height", innerH)
				.attr("fill", band.bg)
				.attr("opacity", 1);
			g.append("line")
				.attr("x1", left)
				.attr("x2", left)
				.attr("y1", 0)
				.attr("y2", innerH)
				.attr("stroke", band.border)
				.attr("stroke-opacity", 0.65)
				.attr("stroke-width", 1.5);
			g.append("line")
				.attr("x1", right)
				.attr("x2", right)
				.attr("y1", 0)
				.attr("y2", innerH)
				.attr("stroke", band.border)
				.attr("stroke-opacity", 0.65)
				.attr("stroke-width", 1.5);
		});

		g.append("g")
			.call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
			.call((ax) => {
				ax.select(".domain").remove();
				ax.selectAll("line").attr("stroke", COLORS.grid);
			});

		g.append("g")
			.call(d3.axisLeft(y).ticks(4).tickFormat((d) => valueFormatter(d)))
			.call((ax) => {
				ax.select(".domain").remove();
				ax.selectAll("line").remove();
				ax.selectAll("text").style("font-size", "10px").style("fill", COLORS.label);
			});

		const step = tickStep(labels.length, 7);
		g.append("g")
			.attr("transform", `translate(0,${innerH})`)
			.call(
				d3
					.axisBottom(x)
					.tickValues(labels.filter((_, i) => i % step === 0 || i === labels.length - 1))
					.tickSize(0)
			)
			.call((ax) => styleAxis(ax, { rotate: labels.length > 9 }));

		// Soft area under first series only
		if (series[0]) {
			const area = d3
				.area()
				.x((d) => x(d.label))
				.y0(innerH)
				.y1((d) => y(d.v0))
				.curve(d3.curveMonotoneX);
			const gradId = `dr-area-${Math.random().toString(36).slice(2, 8)}`;
			const defs = svg.append("defs");
			const grad = defs
				.append("linearGradient")
				.attr("id", gradId)
				.attr("x1", "0")
				.attr("y1", "0")
				.attr("x2", "0")
				.attr("y2", "1");
			grad.append("stop").attr("offset", "0%").attr("stop-color", series[0].color).attr("stop-opacity", 0.25);
			grad.append("stop").attr("offset", "100%").attr("stop-color", series[0].color).attr("stop-opacity", 0.03);
			g.append("path").datum(data).attr("fill", `url(#${gradId})`).attr("d", area);
		}

		series.forEach((s, si) => {
			const line = d3
				.line()
				.x((d) => x(d.label))
				.y((d) => y(d[`v${si}`]))
				.curve(d3.curveMonotoneX);
			g.append("path")
				.datum(data)
				.attr("fill", "none")
				.attr("stroke", s.color)
				.attr("stroke-width", si === 0 ? 2.4 : 2)
				.attr("stroke-linecap", "round")
				.attr("stroke-linejoin", "round")
				.attr("d", line);
		});

		const hover = g.append("g").style("display", "none");
		hover
			.append("line")
			.attr("y1", 0)
			.attr("y2", innerH)
			.attr("stroke", "#b0bec5")
			.attr("stroke-width", 1)
			.attr("stroke-dasharray", "3,3");
		const dots = series.map((s) =>
			hover
				.append("circle")
				.attr("r", 4)
				.attr("fill", s.color)
				.attr("stroke", "#fff")
				.attr("stroke-width", 2)
		);

		g.append("rect")
			.attr("width", innerW)
			.attr("height", innerH)
			.attr("fill", "transparent")
			.style("cursor", "crosshair")
			.on("mouseenter", () => hover.style("display", null))
			.on("mouseleave", () => {
				hover.style("display", "none");
				hideTip();
			})
			.on("mousemove", (event) => {
				const [xm] = d3.pointer(event);
				let nearest = 0;
				let best = Infinity;
				data.forEach((d) => {
					const dist = Math.abs(x(d.label) - xm);
					if (dist < best) {
						best = dist;
						nearest = d.i;
					}
				});
				const d = data[nearest];
				const cx = x(d.label);
				hover.select("line").attr("x1", cx).attr("x2", cx);
				dots.forEach((dot, si) => dot.attr("cx", cx).attr("cy", y(d[`v${si}`])));

				const activeBands = (bands || []).filter(
					(b) => nearest >= b.startIdx && nearest <= b.endIdx
				);
				const bandHtml = activeBands.length
					? `<div style="margin-top:8px;padding-top:6px;border-top:1px solid #455a64">
							${activeBands
								.map(
									(b) => `
								<div style="display:flex;align-items:center;gap:6px;margin-top:4px">
									<span style="width:8px;height:8px;border-radius:2px;background:${b.bg};border:1px solid ${b.border}"></span>
									<span style="color:#fff;font-size:11px">${String(b.name || "Discount")
										.replace(/&/g, "&amp;")
										.replace(/</g, "&lt;")
										.replace(/>/g, "&gt;")}</span>
								</div>
								${b.detailHtml || ""}`
								)
								.join("")}
						</div>`
					: "";

				if (tooltips && tooltips[nearest]) {
					showTip(`${tooltips[nearest]}${bandHtml}`, event);
					return;
				}
				const rows = series
					.map(
						(s, si) =>
							`<div style="display:flex;justify-content:space-between;gap:16px;margin-top:4px">
								<span style="color:#b0bec5"><span style="color:${s.color}">●</span> ${s.label}</span>
								<strong>${valueFormatter(d[`v${si}`])}</strong>
							</div>`
					)
					.join("");
				showTip(
					`<div style="font-weight:500;margin-bottom:2px">${d.label}</div>${rows}${bandHtml}`,
					event
				);
			});
	}, [size.width, labels, series, plotH, valueFormatter, tooltips, bands]);

	return (
		<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
			<SummaryStrip items={summary} />
			<Box ref={ref} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, height: plotH, overflow: "hidden" }}>
				<svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block", maxWidth: "100%" }} />
			</Box>
			<LegendRow items={legend} />
		</Box>
	);
};

/** Channel net sale with discount date-range bands */
export const D3BandLineChart = ({
	labels = [],
	values = [],
	bands = [],
	color = "#2563EB",
	seriesLabel = "Net Sale",
	height = 280,
	valueFormatter = (v) => String(v ?? ""),
}) => {
	const margin = { top: 10, right: 12, bottom: 42, left: 58 };
	const plotH = Math.max(height - 56, 200);
	const [ref, size] = useContainerSize(plotH);
	const svgRef = useRef(null);

	const summary = useMemo(() => {
		const vals = (values || []).map((v) => Number(v) || 0);
		if (!vals.length) return [];
		const peak = d3.max(vals) || 0;
		const latest = vals[vals.length - 1] || 0;
		const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
		return [
			{ key: "peak", label: "Peak day", value: valueFormatter(peak), color },
			{ key: "avg", label: "Daily avg", value: valueFormatter(avg), color: COLORS.ink },
			{ key: "latest", label: "Latest", value: valueFormatter(latest), color },
			{ key: "frames", label: "Discounts", value: String(bands.length), color: COLORS.muted },
		];
	}, [values, bands.length, valueFormatter, color]);

	useEffect(() => {
		const width = size.width;
		if (width < 48 || !labels.length || !svgRef.current) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", plotH).attr("viewBox", `0 0 ${width} ${plotH}`);

		const innerW = width - margin.left - margin.right;
		const innerH = plotH - margin.top - margin.bottom;
		const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

		const data = labels.map((label, i) => ({
			label,
			value: Number(values[i]) || 0,
			i,
		}));
		const maxY = d3.max(data, (d) => d.value) || 0;
		const x = d3.scalePoint().domain(labels).range([0, innerW]).padding(0.12);
		const y = d3
			.scaleLinear()
			.domain([0, maxY * 1.08 || 1])
			.nice()
			.range([innerH, 0]);

		// Discount period bands (calendar days):
		// start date D → left at tick D; end date E → right at tick E+1 (through 23:59:59 of E).
		bands.forEach((band) => {
			const start = Math.min(band.startIdx, band.endIdx);
			const end = Math.max(band.startIdx, band.endIdx);
			const xStart = x(labels[start]);
			const xEnd = x(labels[end]);
			if (xStart == null || xEnd == null) return;
			const xNext = end < labels.length - 1 ? x(labels[end + 1]) : null;
			const step =
				end < labels.length - 1
					? Math.abs((xNext ?? xEnd) - xEnd)
					: end > 0
						? Math.abs(xEnd - (x(labels[end - 1]) ?? xEnd))
						: 8;
			const left = xStart;
			const right = xNext == null ? Math.min(innerW, xEnd + step) : xNext;
			g.append("rect")
				.attr("x", left)
				.attr("y", 0)
				.attr("width", Math.max(right - left, 2))
				.attr("height", innerH)
				.attr("fill", band.bg)
				.attr("opacity", 1);
			g.append("line")
				.attr("x1", left)
				.attr("x2", left)
				.attr("y1", 0)
				.attr("y2", innerH)
				.attr("stroke", band.border)
				.attr("stroke-opacity", 0.65)
				.attr("stroke-width", 1.5);
			g.append("line")
				.attr("x1", right)
				.attr("x2", right)
				.attr("y1", 0)
				.attr("y2", innerH)
				.attr("stroke", band.border)
				.attr("stroke-opacity", 0.65)
				.attr("stroke-width", 1.5);
		});

		g.append("g")
			.call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
			.call((ax) => {
				ax.select(".domain").remove();
				ax.selectAll("line").attr("stroke", COLORS.grid);
			});
		g.append("g")
			.call(d3.axisLeft(y).ticks(4).tickFormat((d) => valueFormatter(d)))
			.call((ax) => {
				ax.select(".domain").remove();
				ax.selectAll("line").remove();
				ax.selectAll("text").style("font-size", "10px").style("fill", COLORS.label);
			});

		const step = tickStep(labels.length, 8);
		g.append("g")
			.attr("transform", `translate(0,${innerH})`)
			.call(
				d3
					.axisBottom(x)
					.tickValues(labels.filter((_, i) => i % step === 0 || i === labels.length - 1))
					.tickSize(0)
			)
			.call((ax) => styleAxis(ax, { rotate: true }));

		const gradId = `dr-band-area-${Math.random().toString(36).slice(2, 8)}`;
		const defs = svg.append("defs");
		const grad = defs
			.append("linearGradient")
			.attr("id", gradId)
			.attr("x1", "0")
			.attr("y1", "0")
			.attr("x2", "0")
			.attr("y2", "1");
		grad.append("stop").attr("offset", "0%").attr("stop-color", color).attr("stop-opacity", 0.22);
		grad.append("stop").attr("offset", "100%").attr("stop-color", color).attr("stop-opacity", 0.03);

		const area = d3
			.area()
			.x((d) => x(d.label))
			.y0(innerH)
			.y1((d) => y(d.value))
			.curve(d3.curveMonotoneX);
		g.append("path").datum(data).attr("fill", `url(#${gradId})`).attr("d", area);

		const line = d3
			.line()
			.x((d) => x(d.label))
			.y((d) => y(d.value))
			.curve(d3.curveMonotoneX);
		g.append("path")
			.datum(data)
			.attr("fill", "none")
			.attr("stroke", color)
			.attr("stroke-width", 2.5)
			.attr("stroke-linecap", "round")
			.attr("d", line);

		const hover = g.append("g").style("display", "none");
		hover
			.append("line")
			.attr("y1", 0)
			.attr("y2", innerH)
			.attr("stroke", "#b0bec5")
			.attr("stroke-dasharray", "3,3");
		const dot = hover
			.append("circle")
			.attr("r", 4.5)
			.attr("fill", color)
			.attr("stroke", "#fff")
			.attr("stroke-width", 2);

		g.append("rect")
			.attr("width", innerW)
			.attr("height", innerH)
			.attr("fill", "transparent")
			.style("cursor", "crosshair")
			.on("mouseenter", () => hover.style("display", null))
			.on("mouseleave", () => {
				hover.style("display", "none");
				hideTip();
			})
			.on("mousemove", (event) => {
				const [xm] = d3.pointer(event);
				let nearest = 0;
				let best = Infinity;
				data.forEach((d) => {
					const dist = Math.abs(x(d.label) - xm);
					if (dist < best) {
						best = dist;
						nearest = d.i;
					}
				});
				const d = data[nearest];
				const cx = x(d.label);
				hover.select("line").attr("x1", cx).attr("x2", cx);
				dot.attr("cx", cx).attr("cy", y(d.value));

				const active = bands.filter((b) => nearest >= b.startIdx && nearest <= b.endIdx);
				const bandBlock = active.length
					? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.12)">
						<div style="color:#90a4ae;font-size:11px;margin-bottom:4px">Active discounts</div>
						${active
							.map((b) => {
								const name = String(b.name || "Scheme")
									.replace(/&/g, "&amp;")
									.replace(/</g, "&lt;")
									.replace(/>/g, "&gt;");
								const extra = b.detailHtml || "";
								return `<div style="margin-top:6px">
									<span style="color:${b.border}">●</span>
									<span style="color:#fff;font-weight:500;word-break:break-word">${name}</span>
									<div style="color:#90a4ae;font-size:11px;margin-left:12px">${b.startLabel} → ${b.endLabel}</div>
									${extra}
								</div>`;
							})
							.join("")}
					</div>`
					: "";

				showTip(
					`<div style="font-weight:500;font-size:13px">${d.label}</div>
					<div style="display:flex;justify-content:space-between;gap:16px;margin-top:6px">
						<span style="color:#b0bec5">${seriesLabel}</span>
						<strong>${valueFormatter(d.value)}</strong>
					</div>${bandBlock}`,
					event
				);
			});
	}, [size.width, labels, values, bands, color, seriesLabel, plotH, valueFormatter]);

	return (
		<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
			<SummaryStrip items={summary} />
			<Box ref={ref} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, height: plotH, overflow: "hidden" }}>
				<svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block", maxWidth: "100%" }} />
			</Box>
		</Box>
	);
};

/** Grouped / single bar */
export const D3BarChart = ({
	labels = [],
	series = [],
	height = 280,
	layout = "vertical",
	valueFormatter = (v) => String(v ?? ""),
	labelMax = 18,
	minBandSize = 0,
	/** Optional per-bar HTML (or plain text) shown on hover — use for full names / details */
	tooltips = null,
	/** Summary strip primary stat: "total" (default) or "avg" (better for %) */
	summaryMode = "total",
}) => {
	const isH = layout === "horizontal";
	const bandMin = minBandSize || (isH ? (series.length > 1 ? 52 : 36) : 0);
	const margin = isH
		? { top: 8, right: 92, bottom: 32, left: 168 }
		: { top: 12, right: 12, bottom: 36, left: 58 };

	const autoPlotH = isH && bandMin
		? Math.max(labels.length * bandMin + margin.top + margin.bottom, 180)
		: Math.max(height - 56, 180);
	const plotH = isH ? autoPlotH : Math.max(height - 40, 180);
	const [ref, size] = useContainerSize(plotH);
	const svgRef = useRef(null);

	const legend = useMemo(
		() => series.map((s) => ({ key: s.label, label: s.label, color: s.color })),
		[series]
	);

	const summary = useMemo(() => {
		if (!series.length) return [];
		const primary = series[0];
		const vals = (primary.values || []).map((v) => Number(v) || 0);
		if (!vals.length) return [];
		const total = vals.reduce((a, b) => a + b, 0);
		const active = vals.filter((v) => v > 0);
		const avg = active.length
			? active.reduce((a, b) => a + b, 0) / active.length
			: vals.length
				? total / vals.length
				: 0;
		const peak = d3.max(vals) || 0;
		const peakIdx = vals.indexOf(peak);
		const primaryStat =
			summaryMode === "avg"
				? {
						key: "avg",
						label: `${primary.label} · avg`,
						value: valueFormatter(avg),
						color: primary.color,
				  }
				: {
						key: "total",
						label: `${primary.label} · total`,
						value: valueFormatter(total),
						color: primary.color,
				  };
		return [
			primaryStat,
			{
				key: "top",
				label: "Highest",
				value: `${truncate(labels[peakIdx], 16)} · ${valueFormatter(peak)}`,
				color: COLORS.ink,
			},
		];
	}, [series, labels, valueFormatter, summaryMode]);

	useEffect(() => {
		const width = size.width;
		if (width < 48 || !labels.length || !series.length || !svgRef.current) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", plotH).attr("viewBox", `0 0 ${width} ${plotH}`);

		const innerW = Math.max(width - margin.left - margin.right, 40);
		const innerH = Math.max(plotH - margin.top - margin.bottom, 40);
		const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

		const tipHtml = (d) => {
			if (tooltips && tooltips[d.index]) return tooltips[d.index];
			return `<div style="font-weight:500">${d.label}</div>
				${series
					.map(
						(ser) =>
							`<div style="margin-top:4px;display:flex;justify-content:space-between;gap:14px">
								<span style="color:#b0bec5"><span style="color:${ser.color}">●</span> ${ser.label}</span>
								<strong>${valueFormatter(ser.values[d.index])}</strong>
							</div>`
					)
					.join("")}`;
		};

		// Unique ids avoid duplicate truncated labels collapsing onto one bar
		const rows = labels.map((label, i) => ({
			id: `row-${i}`,
			label,
			short: truncate(label, labelMax),
			index: i,
		}));
		const maxVal = d3.max(series, (s) => d3.max(s.values, (v) => Number(v) || 0)) || 0;

		if (isH) {
			const y = d3
				.scaleBand()
				.domain(rows.map((r) => r.id))
				.range([0, innerH])
				.padding(0.22);
			const x = d3
				.scaleLinear()
				.domain([0, maxVal * 1.12 || 1])
				.nice()
				.range([0, innerW]);
			const ySub = d3
				.scaleBand()
				.domain(series.map((_, i) => String(i)))
				.range([0, y.bandwidth()])
				.padding(0.15);

			g.append("g")
				.call(
					d3
						.axisLeft(y)
						.tickSize(0)
						.tickFormat((id) => rows.find((r) => r.id === id)?.short || "")
				)
				.call((ax) => {
					ax.select(".domain").attr("stroke", COLORS.domain);
					ax.selectAll("text")
						.style("font-size", "11px")
						.style("fill", COLORS.ink)
						.style("font-weight", 500)
						.style("cursor", "default")
						.each(function eachTick(id) {
							const row = rows.find((r) => r.id === id);
							if (!row) return;
							d3.select(this)
								.on("mousemove", (event) => showTip(tipHtml(row), event))
								.on("mouseleave", hideTip);
						});
				});
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(x).ticks(4).tickFormat((d) => valueFormatter(d)).tickSize(0))
				.call((ax) => styleAxis(ax));

			series.forEach((s, si) => {
				g.selectAll(`.b-${si}`)
					.data(rows)
					.join("rect")
					.attr("class", `b-${si}`)
					.attr("y", (d) => y(d.id) + ySub(String(si)))
					.attr("x", 0)
					.attr("rx", 3)
					.attr("height", Math.max(ySub.bandwidth(), 4))
					.attr("width", (d) => Math.max(x(Number(s.values[d.index]) || 0), 0))
					.attr("fill", s.color)
					.style("cursor", "pointer")
					.on("mousemove", (event, d) => showTip(tipHtml(d), event))
					.on("mouseleave", hideTip);

				// Value label on every series bar (net sale + absorbed discount)
				rows.forEach((d) => {
					const v = Number(s.values[d.index]) || 0;
					if (v <= 0) return;
					const barW = x(v);
					const cy = y(d.id) + ySub(String(si)) + ySub.bandwidth() / 2;
					const label = valueFormatter(v);
					// Prefer label inside bar when wide enough; else just outside
					const inside = barW >= 64;
					g.append("text")
						.attr("x", inside ? barW - 5 : barW + 5)
						.attr("y", cy)
						.attr("text-anchor", inside ? "end" : "start")
						.attr("dominant-baseline", "middle")
						.style("font-size", "10px")
						.style("font-weight", 600)
						.style("fill", inside ? "#ffffff" : s.color)
						.style("pointer-events", "none")
						.text(label);
				});
			});
		} else {
			const short = rows.map((r) => r.short);
			const x = d3.scaleBand().domain(short).range([0, innerW]).padding(0.28);
			const xSub = d3
				.scaleBand()
				.domain(series.map((_, i) => String(i)))
				.range([0, x.bandwidth()])
				.padding(0.12);
			const y = d3
				.scaleLinear()
				.domain([0, maxVal * 1.08 || 1])
				.nice()
				.range([innerH, 0]);

			g.append("g")
				.call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
				.call((ax) => {
					ax.select(".domain").remove();
					ax.selectAll("line").attr("stroke", COLORS.grid);
				});
			g.append("g")
				.call(d3.axisLeft(y).ticks(4).tickFormat((d) => valueFormatter(d)))
				.call((ax) => {
					ax.select(".domain").remove();
					ax.selectAll("line").remove();
					ax.selectAll("text").style("font-size", "10px").style("fill", COLORS.label);
				});
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(x).tickSize(0))
				.call((ax) => {
					styleAxis(ax);
					ax.selectAll("text")
						.style("cursor", "default")
						.each(function eachTick(shortLabel) {
							const row = rows.find((r) => r.short === shortLabel);
							if (!row) return;
							d3.select(this)
								.on("mousemove", (event) => showTip(tipHtml(row), event))
								.on("mouseleave", hideTip);
						});
				});

			series.forEach((s, si) => {
				g.selectAll(`.b-${si}`)
					.data(rows)
					.join("rect")
					.attr("class", `b-${si}`)
					.attr("x", (d) => x(d.short) + xSub(String(si)))
					.attr("y", (d) => y(Number(s.values[d.index]) || 0))
					.attr("rx", 3)
					.attr("width", xSub.bandwidth())
					.attr("height", (d) => Math.max(innerH - y(Number(s.values[d.index]) || 0), 0))
					.attr("fill", s.color)
					.style("cursor", "pointer")
					.on("mousemove", (event, d) => showTip(tipHtml(d), event))
					.on("mouseleave", hideTip);
			});
		}
	}, [size.width, labels, series, plotH, isH, valueFormatter, labelMax, margin, tooltips]);

	const outerHeight = isH ? plotH + 56 : height;

	return (
		<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
			<SummaryStrip items={summary} />
			<Box
				ref={ref}
				sx={{
					width: "100%",
					maxWidth: "100%",
					minWidth: 0,
					height: plotH,
					overflowX: "hidden",
					overflowY: isH && plotH > 480 ? "auto" : "hidden",
				}}>
				<svg ref={svgRef} style={{ width: "100%", height: plotH, display: "block", maxWidth: "100%" }} />
			</Box>
			{series.length > 1 ? <LegendRow items={legend} /> : null}
		</Box>
	);
};

/** Donut with side-style legend values */
export const D3DonutChart = ({
	data = [],
	height = 280,
	valueFormatter = (v) => String(v ?? ""),
}) => {
	const plotH = Math.max(height - 24, 200);
	const [ref, size] = useContainerSize(plotH);
	const svgRef = useRef(null);
	const total = useMemo(() => d3.sum(data, (d) => Number(d.value) || 0) || 0, [data]);

	const legend = useMemo(
		() =>
			data.map((d) => {
				const pct = total ? Math.round(((Number(d.value) || 0) / total) * 100) : 0;
				return {
					key: d.label,
					label: d.label,
					color: d.color,
					meta: `${valueFormatter(d.value)} · ${pct}%`,
				};
			}),
		[data, total, valueFormatter]
	);

	useEffect(() => {
		const width = size.width;
		if (width < 48 || !data.length || !svgRef.current) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", plotH).attr("viewBox", `0 0 ${width} ${plotH}`);

		const radius = Math.min(width, plotH) / 2 - 8;
		const g = svg.append("g").attr("transform", `translate(${width / 2},${plotH / 2})`);

		const pie = d3
			.pie()
			.value((d) => Number(d.value) || 0)
			.sort(null)
			.padAngle(0.025);
		const arc = d3
			.arc()
			.innerRadius(radius * 0.62)
			.outerRadius(radius * 0.92)
			.cornerRadius(5);

		g.selectAll("path")
			.data(pie(data))
			.join("path")
			.attr("d", arc)
			.attr("fill", (d) => d.data.color)
			.style("cursor", "pointer")
			.on("mousemove", (event, d) => {
				const pct = total ? (((Number(d.data.value) || 0) / total) * 100).toFixed(1) : "0";
				showTip(
					`<div style="font-weight:500">${d.data.label}</div>
					<div style="margin-top:4px;display:flex;justify-content:space-between;gap:14px">
						<span style="color:#b0bec5">Share</span><strong>${pct}%</strong>
					</div>
					<div style="margin-top:2px;display:flex;justify-content:space-between;gap:14px">
						<span style="color:#b0bec5">Count</span><strong>${valueFormatter(d.data.value)}</strong>
					</div>`,
					event
				);
			})
			.on("mouseleave", hideTip);

		g.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", "-0.35em")
			.style("font-size", "11px")
			.style("fill", COLORS.muted)
			.style("font-weight", 600)
			.style("letter-spacing", "0.04em")
			.text("TOTAL");
		g.append("text")
			.attr("text-anchor", "middle")
			.attr("dy", "0.95em")
			.style("font-size", "22px")
			.style("font-weight", 700)
			.style("fill", COLORS.ink)
			.text(valueFormatter(total));
	}, [size.width, data, plotH, valueFormatter, total]);

	return (
		<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
			<Box ref={ref} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, height: plotH, overflow: "hidden" }}>
				<svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block", maxWidth: "100%" }} />
			</Box>
			<LegendRow items={legend} />
		</Box>
	);
};

function truncate(text, max = 18) {
	const s = String(text || "");
	return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

const STACK_PALETTE = [
	"#2563EB",
	"#059669",
	"#D97706",
	"#7C3AED",
	"#0891B2",
	"#DC2626",
	"#DB2777",
	"#0D9488",
	"#EA580C",
	"#4F46E5",
	"#16A34A",
	"#CA8A04",
];

/**
 * Stacked bar for contribution breakdown.
 * rows: [{ category: 'EBO', segments: { 'RARE RABBIT': 100, 'RAREISM': 50 } }]
 * OR pass categories + seriesKeys + matrix[categoryIndex][seriesIndex]
 *
 * props:
 *  categories: string[]  (x-axis / band axis)
 *  series: [{ key, label, color?, values: number[] }]  values aligned to categories
 *  layout: 'vertical' | 'horizontal'
 */
export const D3StackedBarChart = ({
	categories = [],
	series = [],
	height = 320,
	layout = "vertical",
	valueFormatter = (v) => String(v ?? ""),
	labelMax = 16,
}) => {
	const isH = layout === "horizontal";
	const margin = isH
		? { top: 8, right: 24, bottom: 28, left: 100 }
		: { top: 12, right: 12, bottom: 40, left: 58 };
	const plotH = Math.max(height - 56, 200);
	const [ref, size] = useContainerSize(plotH);
	const svgRef = useRef(null);

	const coloredSeries = useMemo(
		() =>
			series.map((s, i) => ({
				...s,
				color: s.color || STACK_PALETTE[i % STACK_PALETTE.length],
			})),
		[series]
	);

	const legend = useMemo(
		() =>
			coloredSeries.map((s) => ({
				key: s.key || s.label,
				label: s.label,
				color: s.color,
			})),
		[coloredSeries]
	);

	const summary = useMemo(() => {
		if (!coloredSeries.length || !categories.length) return [];
		const totals = categories.map((_, ci) =>
			coloredSeries.reduce((sum, s) => sum + (Number(s.values[ci]) || 0), 0)
		);
		const grand = totals.reduce((a, b) => a + b, 0);
		const peakIdx = totals.indexOf(d3.max(totals) || 0);
		return [
			{
				key: "total",
				label: "Total net sale",
				value: valueFormatter(grand),
				color: COLORS.ink,
			},
			{
				key: "top",
				label: "Highest",
				value: `${truncate(categories[peakIdx], 14)} · ${valueFormatter(totals[peakIdx] || 0)}`,
				color: COLORS.ink,
			},
		];
	}, [coloredSeries, categories, valueFormatter]);

	useEffect(() => {
		const width = size.width;
		if (width < 48 || !categories.length || !coloredSeries.length || !svgRef.current) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();
		svg.attr("width", width).attr("height", plotH).attr("viewBox", `0 0 ${width} ${plotH}`);

		const innerW = width - margin.left - margin.right;
		const innerH = plotH - margin.top - margin.bottom;
		const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

		const short = categories.map((c) => truncate(c, labelMax));
		const keys = coloredSeries.map((s) => s.key || s.label);
		const color = d3
			.scaleOrdinal()
			.domain(keys)
			.range(coloredSeries.map((s) => s.color));

		const stackedData = categories.map((cat, ci) => {
			const row = { category: cat, short: short[ci] };
			coloredSeries.forEach((s) => {
				row[s.key || s.label] = Number(s.values[ci]) || 0;
			});
			return row;
		});

		const stack = d3.stack().keys(keys);
		const seriesData = stack(stackedData);
		const maxY = d3.max(seriesData, (layer) => d3.max(layer, (d) => d[1])) || 0;

		if (isH) {
			const y = d3.scaleBand().domain(short).range([0, innerH]).padding(0.28);
			const x = d3
				.scaleLinear()
				.domain([0, maxY * 1.05 || 1])
				.nice()
				.range([0, innerW]);

			g.append("g")
				.call(d3.axisLeft(y).tickSize(0))
				.call((ax) => {
					ax.select(".domain").attr("stroke", COLORS.domain);
					ax.selectAll("text").style("font-size", "11px").style("fill", COLORS.ink).style("font-weight", 500);
				});
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(x).ticks(4).tickFormat((d) => valueFormatter(d)).tickSize(0))
				.call((ax) => styleAxis(ax));

			g.selectAll("g.layer")
				.data(seriesData)
				.join("g")
				.attr("class", "layer")
				.attr("fill", (d) => color(d.key))
				.selectAll("rect")
				.data((d) => d)
				.join("rect")
				.attr("y", (d) => y(d.data.short))
				.attr("x", (d) => x(d[0]))
				.attr("width", (d) => Math.max(x(d[1]) - x(d[0]), 0))
				.attr("height", y.bandwidth())
				.attr("rx", 1)
				.style("cursor", "pointer")
				.on("mousemove", (event, d) => {
					const key = d3.select(event.currentTarget.parentNode).datum().key;
					const seriesMeta = coloredSeries.find((s) => (s.key || s.label) === key);
					const val = d[1] - d[0];
					const total = keys.reduce((sum, k) => sum + (Number(d.data[k]) || 0), 0);
					const pct = total ? ((val / total) * 100).toFixed(1) : "0";
					showTip(
						`<div style="font-weight:500">${d.data.category}</div>
						<div style="margin-top:6px;display:flex;justify-content:space-between;gap:14px">
							<span style="color:#b0bec5"><span style="color:${seriesMeta?.color}">●</span> ${seriesMeta?.label || key}</span>
							<strong>${valueFormatter(val)}</strong>
						</div>
						<div style="margin-top:2px;color:#90a4ae">${pct}% of this bar</div>
						<div style="margin-top:2px;color:#90a4ae">Bar total ${valueFormatter(total)}</div>`,
						event
					);
				})
				.on("mouseleave", hideTip);

			// Total label at end of each stack
			stackedData.forEach((row) => {
				const total = keys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0);
				if (!total) return;
				g.append("text")
					.attr("x", x(total) + 6)
					.attr("y", y(row.short) + y.bandwidth() / 2)
					.attr("dominant-baseline", "middle")
					.style("font-size", "10px")
					.style("font-weight", 600)
					.style("fill", COLORS.label)
					.text(valueFormatter(total));
			});
		} else {
			const x = d3.scaleBand().domain(short).range([0, innerW]).padding(0.28);
			const y = d3
				.scaleLinear()
				.domain([0, maxY * 1.08 || 1])
				.nice()
				.range([innerH, 0]);

			g.append("g")
				.call(d3.axisLeft(y).ticks(4).tickSize(-innerW).tickFormat(""))
				.call((ax) => {
					ax.select(".domain").remove();
					ax.selectAll("line").attr("stroke", COLORS.grid);
				});
			g.append("g")
				.call(d3.axisLeft(y).ticks(4).tickFormat((d) => valueFormatter(d)))
				.call((ax) => {
					ax.select(".domain").remove();
					ax.selectAll("line").remove();
					ax.selectAll("text").style("font-size", "10px").style("fill", COLORS.label);
				});
			g.append("g")
				.attr("transform", `translate(0,${innerH})`)
				.call(d3.axisBottom(x).tickSize(0))
				.call((ax) => styleAxis(ax));

			g.selectAll("g.layer")
				.data(seriesData)
				.join("g")
				.attr("class", "layer")
				.attr("fill", (d) => color(d.key))
				.selectAll("rect")
				.data((d) => d)
				.join("rect")
				.attr("x", (d) => x(d.data.short))
				.attr("y", (d) => y(d[1]))
				.attr("height", (d) => Math.max(y(d[0]) - y(d[1]), 0))
				.attr("width", x.bandwidth())
				.attr("rx", 1)
				.style("cursor", "pointer")
				.on("mousemove", (event, d) => {
					const key = d3.select(event.currentTarget.parentNode).datum().key;
					const seriesMeta = coloredSeries.find((s) => (s.key || s.label) === key);
					const val = d[1] - d[0];
					const total = keys.reduce((sum, k) => sum + (Number(d.data[k]) || 0), 0);
					const pct = total ? ((val / total) * 100).toFixed(1) : "0";
					showTip(
						`<div style="font-weight:500">${d.data.category}</div>
						<div style="margin-top:6px;display:flex;justify-content:space-between;gap:14px">
							<span style="color:#b0bec5"><span style="color:${seriesMeta?.color}">●</span> ${seriesMeta?.label || key}</span>
							<strong>${valueFormatter(val)}</strong>
						</div>
						<div style="margin-top:2px;color:#90a4ae">${pct}% of this bar</div>
						<div style="margin-top:2px;color:#90a4ae">Bar total ${valueFormatter(total)}</div>`,
						event
					);
				})
				.on("mouseleave", hideTip);

			stackedData.forEach((row) => {
				const total = keys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0);
				if (!total) return;
				g.append("text")
					.attr("x", x(row.short) + x.bandwidth() / 2)
					.attr("y", y(total) - 6)
					.attr("text-anchor", "middle")
					.style("font-size", "10px")
					.style("font-weight", 600)
					.style("fill", COLORS.label)
					.text(valueFormatter(total));
			});
		}
	}, [
		size.width,
		categories,
		coloredSeries,
		plotH,
		isH,
		valueFormatter,
		labelMax,
		margin,
	]);

	return (
		<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
			<SummaryStrip items={summary} />
			<Box ref={ref} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, height: plotH, overflow: "hidden" }}>
				<svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block", maxWidth: "100%" }} />
			</Box>
			<LegendRow items={legend} />
		</Box>
	);
};
